import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Chip,
  Button,
} from "@mui/material";
import {
  CalendarMonth as CalendarMonthIcon,
  CalendarToday as CalendarIcon,
  Star as StarIcon,
  CheckCircleOutline as CheckIcon,
} from "@mui/icons-material";
import PageContainer from "../common/PageContainer";
import PageHeader from "../common/PageHeader";
import EmptyState from "../common/EmptyState";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const HolidaysListForEmployees = () => {
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [nearestHolidayId, setNearestHolidayId] = useState(null);
  const [currentYear] = useState(new Date().getFullYear());

  const sortHolidaysByDate = (holidays) => {
    return holidays.sort((a, b) => new Date(a.date) - new Date(b.date));
  };

  const findNearestHoliday = (sortedHolidays) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return sortedHolidays.find((holiday) => new Date(holiday.date) >= today)
      ?._id;
  };

  useEffect(() => {
    const fetchHolidays = async () => {
      try {
        const token = sessionStorage.getItem("token");
        const response = await fetch(`${BASE_URL}/api/holidays`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) throw new Error(await response.text());

        const data = await response.json();
        const sortedHolidays = sortHolidaysByDate(data);
        setHolidays(sortedHolidays);
        setNearestHolidayId(findNearestHoliday(sortedHolidays));
      } catch (err) {
        console.error("Error fetching holidays:", err);
        setError(err.message || "Failed to load holiday data");
      } finally {
        setLoading(false);
      }
    };

    fetchHolidays();
  }, []);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      month: "short",
      day: "numeric",
    });
  };

  const getDaysUntilHoliday = (dateString) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const holidayDate = new Date(dateString);
    const diffTime = holidayDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return "Today!";
    if (diffDays === 1) return "Tomorrow!";
    if (diffDays < 7) return `${diffDays} days to go`;
    return `Coming in ${Math.floor(diffDays / 7)} week${
      Math.floor(diffDays / 7) > 1 ? "s" : ""
    }`;
  };

  const getMonthFromDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", { month: "short" });
  };

  const getDayFromDate = (dateString) => {
    const date = new Date(dateString);
    return date.getDate();
  };

  const nearestHoliday = holidays.find((h) => h._id === nearestHolidayId);
  const upcomingHolidays = holidays.filter(
    (h) => new Date(h.date) >= new Date() && h._id !== nearestHolidayId
  );
  const pastHolidays = holidays.filter((h) => new Date(h.date) < new Date());

  return (
    <PageContainer>
      <PageHeader
        gradient
        title={`Company Holidays ${currentYear}`}
        subtitle="Plan your year with the company holiday calendar"
        icon={<CalendarMonthIcon />}
      />

      {/* Loading State */}
      {loading && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            py: 10,
            gap: 2,
          }}
        >
          <CircularProgress size={40} sx={{ color: "#667eea" }} />
          <Typography sx={{ color: "#8b8fa3", fontSize: "0.875rem" }}>
            Loading holiday calendar...
          </Typography>
        </Box>
      )}

      {/* Error State */}
      {!loading && error && (
        <Paper
          elevation={0}
          sx={{
            border: "1px solid #FCA5A5",
            borderRadius: "16px",
            bgcolor: "#FEF2F2",
            p: 4,
            textAlign: "center",
          }}
        >
          <Typography
            sx={{ fontWeight: 600, color: "#DC2626", fontSize: "1rem", mb: 1 }}
          >
            Something went wrong
          </Typography>
          <Typography
            sx={{ color: "#8b8fa3", fontSize: "0.875rem", mb: 2.5 }}
          >
            {error}
          </Typography>
          <Button
            variant="outlined"
            onClick={() => window.location.reload()}
            sx={{
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 500,
              color: "#DC2626",
              borderColor: "#FCA5A5",
              "&:hover": { bgcolor: "#FEF2F2", borderColor: "#DC2626" },
            }}
          >
            Try Again
          </Button>
        </Paper>
      )}

      {/* Content */}
      {!loading && !error && holidays.length === 0 && (
        <EmptyState
          icon={<CalendarMonthIcon />}
          title="No Holidays Found"
          subtitle="No company holidays have been added for this year yet."
        />
      )}

      {!loading && !error && holidays.length > 0 && (
        <>
          {/* Featured Nearest Holiday */}
          {nearestHoliday && (
            <Paper
              elevation={0}
              sx={{
                border: "1px solid #eef0f6",
                borderLeft: "4px solid #667eea",
                borderRadius: "16px",
                p: 3,
                mb: 3,
                bgcolor: "#f5f7fb",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: 1.5,
                }}
              >
                <StarIcon sx={{ fontSize: 18, color: "#667eea" }} />
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: "0.8125rem",
                    color: "#667eea",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                  }}
                >
                  Next Holiday
                </Typography>
              </Box>
              <Typography
                sx={{ fontWeight: 700, fontSize: "1.25rem", color: "#1a1a2e", mb: 0.75 }}
              >
                {nearestHoliday.name}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                <CalendarIcon sx={{ fontSize: 16, color: "#8b8fa3" }} />
                <Typography sx={{ color: "#1a1a2e", fontSize: "0.875rem" }}>
                  {formatDate(nearestHoliday.date)}
                </Typography>
              </Box>
              <Typography sx={{ color: "#667eea", fontSize: "0.8125rem", fontWeight: 500, mt: 1 }}>
                {getDaysUntilHoliday(nearestHoliday.date)}
              </Typography>
            </Paper>
          )}

          {/* Upcoming Holidays */}
          {upcomingHolidays.length > 0 && (
            <>
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: "1rem",
                  color: "#1a1a2e",
                  mb: 2,
                  mt: 1,
                }}
              >
                Upcoming Holidays
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(2, 1fr)",
                    md: "repeat(3, 1fr)",
                  },
                  gap: 2,
                  mb: 4,
                }}
              >
                {upcomingHolidays.map((holiday) => (
                  <Paper
                    key={holiday._id}
                    elevation={0}
                    sx={{
                      border: "1px solid #eef0f6",
                      borderRadius: "16px",
                      p: 3,
                      transition: "box-shadow 0.2s ease",
                      "&:hover": {
                        boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
                      },
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: "10px",
                          bgcolor: "#eef0ff",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "0.625rem",
                            fontWeight: 600,
                            color: "#667eea",
                            textTransform: "uppercase",
                            lineHeight: 1,
                          }}
                        >
                          {getMonthFromDate(holiday.date)}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "1.1rem",
                            fontWeight: 700,
                            color: "#667eea",
                            lineHeight: 1.2,
                          }}
                        >
                          {getDayFromDate(holiday.date)}
                        </Typography>
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          sx={{
                            fontWeight: 600,
                            fontSize: "0.9375rem",
                            color: "#1a1a2e",
                            lineHeight: 1.3,
                          }}
                        >
                          {holiday.name}
                        </Typography>
                        <Typography
                          sx={{ color: "#8b8fa3", fontSize: "0.8125rem", mt: 0.25 }}
                        >
                          {formatDate(holiday.date)}
                        </Typography>
                        <Typography
                          sx={{
                            color: "#667eea",
                            fontSize: "0.75rem",
                            fontWeight: 500,
                            mt: 0.5,
                          }}
                        >
                          {getDaysUntilHoliday(holiday.date)}
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                ))}
              </Box>
            </>
          )}

          {/* Past Holidays */}
          {pastHolidays.length > 0 && (
            <>
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: "1rem",
                  color: "#1a1a2e",
                  mb: 2,
                }}
              >
                Past Holidays
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(2, 1fr)",
                    md: "repeat(3, 1fr)",
                  },
                  gap: 2,
                }}
              >
                {pastHolidays.map((holiday) => (
                  <Paper
                    key={holiday._id}
                    elevation={0}
                    sx={{
                      border: "1px solid #eef0f6",
                      borderRadius: "16px",
                      p: 3,
                      opacity: 0.7,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: "10px",
                          bgcolor: "#f5f7fb",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "0.625rem",
                            fontWeight: 600,
                            color: "#8b8fa3",
                            textTransform: "uppercase",
                            lineHeight: 1,
                          }}
                        >
                          {getMonthFromDate(holiday.date)}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "1.1rem",
                            fontWeight: 700,
                            color: "#8b8fa3",
                            lineHeight: 1.2,
                          }}
                        >
                          {getDayFromDate(holiday.date)}
                        </Typography>
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Typography
                            sx={{
                              fontWeight: 600,
                              fontSize: "0.9375rem",
                              color: "#1a1a2e",
                              lineHeight: 1.3,
                            }}
                          >
                            {holiday.name}
                          </Typography>
                          <Chip
                            icon={<CheckIcon sx={{ fontSize: 14 }} />}
                            label="Passed"
                            size="small"
                            sx={{
                              height: 22,
                              fontSize: "0.6875rem",
                              bgcolor: "#f5f7fb",
                              color: "#8b8fa3",
                              "& .MuiChip-icon": { color: "#8b8fa3" },
                            }}
                          />
                        </Box>
                        <Typography
                          sx={{ color: "#8b8fa3", fontSize: "0.8125rem", mt: 0.25 }}
                        >
                          {formatDate(holiday.date)}
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                ))}
              </Box>
            </>
          )}
        </>
      )}
    </PageContainer>
  );
};

export default HolidaysListForEmployees;
